/**
 * Technical descriptions for the Database refresh picker.
 * Shown under each checkbox so operators know exactly what runs.
 */

export const REFRESH_TARGET_LABELS = Object.freeze({
  'phaser-fpv': 'Phaser FPV — Shopify product cache',
  'central-coast-coffee': 'Central Coast Coffee — Shopify product cache',
  'porlex-australia': 'Porlex Australia — Shopify product cache',
  'ace176-c7': 'Redeemer Coffee — Shopify product cache',
  'purchase-orders-phaser': 'Phaser PO cache (Lightspeed Retail)',
  'purchase-orders-ccc': 'CCC PO cache (Qoblox / Incoming Stock)',
  'qoblox-suppliers': 'Qoblox supplier directory',
  'inventory-planner': 'Recalculate forecast + buy lists',
  'planner-catalog-ccc': 'CCC planner catalog (Shopify cache → offers)',
  'planner-sales-phaser': 'Planner full ingest — Phaser FPV',
  'planner-sales-ccc': 'Planner full ingest — Central Coast Coffee',
  'planner-cost-backfill': 'Lightspeed average-cost sync (Phaser SKUs)',
  'planner-supplier-skus': 'Lightspeed supplier SKU / vendor code sync'
});

/** Short one-liner for progress panel headers. */
export const REFRESH_TARGET_SUMMARY = Object.freeze({
  'phaser-fpv': 'Shopify Admin API → Firestore `products` (warehouse only).',
  'central-coast-coffee': 'Shopify Admin API → Firestore `products` (warehouse only).',
  'porlex-australia': 'Shopify Admin API → Firestore `products` (warehouse only).',
  'ace176-c7': 'Shopify Admin API → Firestore `products` (warehouse only).',
  'purchase-orders-phaser': 'Lightspeed consignments → Incoming Stock PO cache.',
  'purchase-orders-ccc': 'Qoblox purchase orders → Incoming Stock PO cache.',
  'qoblox-suppliers': 'Qoblox `/suppliers` → Firestore supplier name map.',
  'inventory-planner': 'Forecast + optimiser on existing planner data (no ingest).',
  'planner-catalog-ccc': 'Shopify cache → merge → `planner_supplier_offers` + `planner_skus`.',
  'planner-sales-phaser': 'Lightspeed sales + stock + offers (full channel ingest).',
  'planner-sales-ccc': 'Qoblox sales + catalog + Shopify merge (full channel ingest).',
  'planner-cost-backfill': 'Lightspeed inventory_levels average cost → planner offers.',
  'planner-supplier-skus': 'Lightspeed product supplier codes → planner_supplier_offers.supplierSku.'
});

/** Full technical step list shown under each checkbox. */
export const REFRESH_TARGET_DETAILS = Object.freeze({
  'phaser-fpv': [
    'API: Shopify Admin GraphQL Bulk Operations (store phaser-fpv.myshopify.com).',
    'Pulls all products and variants via 5 concurrent bulk jobs, plus a metafields pass per job.',
    'Writes each variant to Firestore collection `products` (doc id = store domain + variant legacy id).',
    'Updates: title, SKU, barcode, tags, product type, vendor, images, warehouse location, summed on-hand inventory.',
    'Also writes a dated restore snapshot per changed variant to `products_backup` (see docs/SHOPIFY_RESTORE_RUNBOOK.md).',
    'Also syncs Shopify smart collection rules to Firestore `shopify_collections`.',
    'Used by: Products grid, picking, preorders display, labels, warehouse map.',
    'Does NOT update Inventory Planner sales, offers, or buy lists.'
  ],
  'central-coast-coffee': [
    'API: Shopify Admin (store central-coast-coffee.myshopify.com, version 2024-10).',
    'Pulls all products and variants; paginates until complete.',
    'Writes to Firestore `products` with SKU, title, product type (supplier name on CCC), vendor, tags, on-hand stock.',
    'This is the warehouse product cache — required before CCC planner catalog sync.',
    'Product type on CCC = supplier identity (e.g. Roest) used by Inventory Planner.',
    'Does NOT copy SKUs into `planner_supplier_offers` by itself — run CCC planner catalog sync after this for new SKUs like p3000 variants.'
  ],
  'porlex-australia': [
    'API: Shopify Admin (store porlex-australia.myshopify.com).',
    'Full product + variant sync into Firestore `products`.',
    'Warehouse module only — Porlex is not on Inventory Planner.'
  ],
  'ace176-c7': [
    'API: Shopify Admin (store ace176-c7.myshopify.com, Redeemer Coffee).',
    'Full product + variant sync into Firestore `products`.',
    'Warehouse module only.'
  ],
  'purchase-orders-phaser': [
    'API: Lightspeed Retail X-Series (`/consignments` / purchase orders).',
    'Pulls the newest N POs (slice size from Settings → Incoming Stock, default 20).',
    'Writes to Firestore `system_cache/purchase_orders` and local JSON cache.',
    'Includes line SKUs, qty, supplier, status, costs where available.',
    'Incoming Stock workflow tab comes from Firestore `po_metadata` — not auto-mapped from Lightspeed status.',
    'Does NOT update Inventory Planner on-order math directly (planner reads open PO qty separately).'
  ],
  'purchase-orders-ccc': [
    'API: Qoblox `GET /purchase_orders` (api.qoblex.com, QOBLEX_API_KEY).',
    'Pulls newest N CCC POs (default slice 20) with line items and SKUs.',
    'Merges into Firestore `system_cache/purchase_orders` CCC section + disk cache.',
    'Resolves supplier names from Firestore `system_cache/qoblox_suppliers` map.',
    'Used by: Incoming Stock module tabs (Drafts, Waiting Arrival, etc.).',
    'Does NOT run planner sales ingest or rebuild planner offers.'
  ],
  'qoblox-suppliers': [
    'API: Qoblox `GET /suppliers` (paginated, rate-limited 10 req/min).',
    'Writes id → name map to Firestore `system_cache/qoblox_suppliers`.',
    'Used to replace numeric supplier ids (e.g. Supplier 4987063) on CCC PO cards.',
    'Also runs automatically before CCC PO cache refresh if directory is stale or empty.'
  ],
  'inventory-planner': [
    'API: internal `POST /api/planner/scheduler/run` with skipIngest=true.',
    'Step 1 — Forecast: recalculates demand velocity, safety stock, reorder points for all SKUs in `planner_skus` (both channels).',
    'Step 2 — Optimiser: rebuilds daily buy lists in `planner_recommendations` respecting monthly spend cap and supplier MOV rules.',
    'Uses existing sales history already in Firestore — does NOT call Qoblox or Lightspeed.',
    'Does NOT rebuild supplier catalog or offers — run CCC planner catalog sync first if SKUs are missing from manual order lists.',
    'Typical runtime: seconds to a few minutes depending on SKU count.'
  ],
  'planner-catalog-ccc': [
    'Reads typed CCC SKUs from Firestore `products` (central-coast-coffee store) — run Shopify product sync first if cache is stale.',
    'Supplier identity from Shopify product type (e.g. Roest → supplier id `roest`).',
    'Overlays unit cost and supplier SKU from existing `planner_supplier_offers` where already known.',
    'For brand-new SKUs with no cost: targeted Qoblox variant lookup by SKU (up to 25 SKUs, not a full catalog crawl).',
    'Merges Shopify-primary catalog with persisted planner data; builds offer rows.',
    'Writes Firestore `planner_supplier_offers` (what Manual Order / supplier lists read) and `planner_skus` (on-hand, titles, preserves existing sales velocity fields).',
    'Does NOT pull sales history — use Planner full ingest — CCC when velocity needs updating.',
    'Fast path for new SKUs (e.g. p3000_3phase, p3000_single_phase) after a Shopify product sync.'
  ],
  'planner-sales-phaser': [
    'Background job: `POST /api/planner/sync/start` channel=phaserfpv.',
    'Step 1 — Sales sync: incremental Lightspeed sales pull → updates `planner_skus` velocity and `planner_revenue_monthly`.',
    'Step 2 — Catalog: Lightspeed products/inventory → supplier offers in `planner_supplier_offers`.',
    'Step 3 — Upsert: stock levels, titles, inbound PO qty into `planner_skus`.',
    'Long-running — can take many minutes on first run or large catch-up.',
    'Use when sales velocity or Phaser stock/costs have changed materially.'
  ],
  'planner-sales-ccc': [
    'Background job: `POST /api/planner/sync/start` channel=centralcoastcoffee.',
    'Step 1 — Sales sync: incremental Qoblox sale_orders pull → updates sales velocity in `planner_skus`.',
    'Step 2 — Catalog: full Qoblox `/variants` paginated crawl (slow, rate-limited).',
    'Step 3 — Shopify merge: typed SKUs from Firestore product cache merged as supplier identity source.',
    'Step 4 — Offers: writes `planner_supplier_offers`; upserts `planner_skus` with stock and titles.',
    'Use for sales history catch-up or full Qoblox cost refresh — prefer CCC planner catalog sync for SKU-only updates.'
  ],
  'planner-cost-backfill': [
    'Background job: `POST /api/planner/costs/backfill/start`.',
    'API: Lightspeed `GET /inventory_levels/{productId}` → aggregate average_cost across outlets.',
    'Updates unit cost on matching Phaser FPV rows in `planner_supplier_offers`.',
    'Runs server-side in batches — continues if you close the browser tab.',
    'Does not touch CCC / Qoblox SKUs.'
  ],
  'planner-supplier-skus': [
    'Background job: `POST /api/planner/supplier-skus/sync/start`.',
    'API: paginated Lightspeed `GET /products` only — reads `product_suppliers[].code` and top-level `supplier_code`.',
    'Writes `supplierSku` (vendor ref) on `planner_supplier_offers` for Phaser FPV — no inventory API, no per-SKU lookups.',
    'Typical runtime ~1–2 min for the full catalogue (~16k SKUs).',
    'After sync, run Recalculate buy lists so daily baskets pick up vendor refs; new drafts/emails use codes immediately.',
    'Does not touch CCC / Qoblox (Qoblox supplier SKUs come from catalog ingest).'
  ]
});

export const REFRESH_STORE_KEYS = [
  'phaser-fpv',
  'central-coast-coffee',
  'porlex-australia',
  'ace176-c7',
  'purchase-orders-phaser',
  'purchase-orders-ccc',
  'qoblox-suppliers',
  'inventory-planner',
  'planner-catalog-ccc',
  'planner-sales-phaser',
  'planner-sales-ccc',
  'planner-cost-backfill',
  'planner-supplier-skus'
];

/** Module-grouped layout for the refresh picker (matches nav modules). */
export const REFRESH_MODULE_GROUPS = Object.freeze([
  {
    id: 'products',
    moduleKey: 'warehouse',
    title: 'Products',
    description: 'Shopify product caches for the Products module — grid, picking, labels, tags, and warehouse map. Also the source for CCC planner catalog sync.',
    sections: [
      {
        targetKeys: ['phaser-fpv', 'central-coast-coffee', 'porlex-australia', 'ace176-c7']
      }
    ]
  },
  {
    id: 'incoming-stock',
    moduleKey: 'po',
    title: 'Incoming Stock',
    description: 'PO list and supplier caches for the Incoming Stock module — Drafts, Waiting Arrival, receive workflow. Separate from Inventory Planner draft POs.',
    sections: [
      {
        targetKeys: ['purchase-orders-phaser', 'purchase-orders-ccc', 'qoblox-suppliers']
      }
    ]
  },
  {
    id: 'inventory-planner',
    moduleKey: 'po-planning',
    title: 'Inventory Planner',
    description: 'Forecast, catalog, sales ingest, and cost sync for the Inventory Planner module. Requires Inventory Planner permission.',
    permission: 'poPlanning',
    sections: [
      {
        label: 'Recalculate only',
        hint: 'Forecast + buy lists on existing Firestore data. No live API pulls.',
        targetKeys: ['inventory-planner']
      },
      {
        label: 'CCC catalog (fast)',
        hint: 'Shopify product cache → planner offers. Run after CCC Products sync for new SKUs.',
        targetKeys: ['planner-catalog-ccc']
      },
      {
        label: 'Full ingest & costs (background)',
        hint: 'Long-running jobs — live Lightspeed or Qoblox API pulls.',
        targetKeys: ['planner-sales-phaser', 'planner-sales-ccc', 'planner-cost-backfill', 'planner-supplier-skus']
      }
    ]
  }
]);

const SHOPIFY_STORE_DOMAIN_BY_TARGET = Object.freeze({
  'phaser-fpv': 'phaser-fpv.myshopify.com',
  'central-coast-coffee': 'central-coast-coffee.myshopify.com',
  'porlex-australia': 'porlex-australia.myshopify.com',
  'ace176-c7': 'ace176-c7.myshopify.com'
});

export function getShopifyStoreDomainForRefreshTarget(targetKey) {
  return SHOPIFY_STORE_DOMAIN_BY_TARGET[targetKey] || null;
}
